/**
 * sellerPermissions — what the signed-in person may change in THIS store (feature 0073).
 *
 * The editor used to know no rights at all. Its own routes all follow `theme:manage`, which is the door the
 * dashboard checks before opening it, so that was enough — until a panel started writing to a route guarded
 * by ANOTHER box. The countdown design panel saves through `PUT /api/builder/countdown/{id}/design`, which
 * follows `countdown:manage`: an employee without that box moved the sliders, watched the canvas repaint,
 * and only then read « Could not save ». The panel now asks first.
 *
 * ## One source, the same as the dashboard
 *
 * `GET /api/auth/seller/me` carries `permissions` and `storeOwner`, read by the SAME port the server's
 * interceptor judges with. A second source here would end up disagreeing with the one that refuses.
 *
 * ## What it never does
 *
 * Guard anything. The server is the guarantee; this only decides what the panel offers. So a check that
 * cannot answer (offline, a 500, a malformed body) answers « allowed »: the save still goes out, the server
 * still refuses, and the panel still says so. Guessing « refused » would lock an owner out of her own editor
 * on a network blip.
 *
 * And it never runs in a superadmin consultation: that token is a borrowed, read-only view of one store,
 * `/api/auth/seller/*` is not its surface, and `http.ts` already blocks every write it would attempt.
 */
import { useEffect, useState } from 'react';

import { isReadOnlyView } from '../boot/view-handoff';
import { getStoreId } from '../boot/session';
import { apiRequest } from './http';

/** The rights of the signed-in person, or `null` when they could not be read. */
export interface SellerRights {
  readonly owner: boolean;
  readonly codes: ReadonlySet<string>;
}

/** `GET /api/auth/seller/me`, read defensively — only the two fields this module needs. */
interface MeBody {
  readonly permissions?: unknown;
  readonly storeOwner?: unknown;
}

/**
 * One read per store: the token's store can change under the editor (switch in another tab). The store
 * is read from the token only to KEY this cache — it is never sent anywhere.
 */
let cache: { scope: string | null; rights: Promise<SellerRights | null> } | null = null;

export function loadSellerRights(): Promise<SellerRights | null> {
  const scope = getStoreId();
  if (cache && cache.scope === scope) return cache.rights;
  const rights = apiRequest<MeBody>('/api/auth/seller/me')
    .then((body): SellerRights | null => {
      if (!body || !Array.isArray(body.permissions)) return null;
      return {
        owner: body.storeOwner === true,
        codes: new Set(body.permissions.filter((c): c is string => typeof c === 'string')),
      };
    })
    .catch(() => null);
  cache = { scope, rights };
  return rights;
}

/** Tests only: forget what was read. */
export function resetSellerRightsForTests(): void {
  cache = null;
}

/** May these rights do `code`? Unknown rights answer yes — see the header. */
export function rightsAllow(rights: SellerRights | null, code: string): boolean {
  if (rights === null) return true;
  return rights.owner || rights.codes.has(code);
}

/**
 * `'pending'` until the rights are read, then `'yes'` or `'no'`. A superadmin consultation answers `'yes'`
 * without asking: its writes are refused by `http.ts` with their own message.
 */
export function useSellerCan(code: string): 'pending' | 'yes' | 'no' {
  const [answer, setAnswer] = useState<'pending' | 'yes' | 'no'>(() =>
    isReadOnlyView() ? 'yes' : 'pending',
  );
  useEffect(() => {
    if (isReadOnlyView()) return undefined;
    let alive = true;
    void loadSellerRights().then((rights) => {
      if (alive) setAnswer(rightsAllow(rights, code) ? 'yes' : 'no');
    });
    return () => {
      alive = false;
    };
  }, [code]);
  return answer;
}
